/* Copyright (c) 2020-present CloudIO Inc */

import {
  CompatibleFieldTypes,
  FieldType,
  FieldTypeArray,
  FieldTypeDesc,
  IoDatasource,
  isDateType,
  ReferenceMetadata,
} from '@cloudio-saas/datasource-types';
import {
  emptyFunction,
  useStateXAtom,
  useStateXAtomValue,
} from '@cloudio-saas/statex';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FieldTypeIcons } from 'common/DatasourceAttributeTypeIcons';
import Icon, { parseIconText } from 'icons/Icon';
import {
  sidebarActiveAttributeIdAtom,
  sidebarActiveDatasourceIdAtom,
} from 'layout/Sidebar/atoms';
import { dsAttributesFilter } from 'layout/Sidebar/panels/DataSourceView/AttributesList';
import { camelCase, snakeCase, startCase } from 'lodash-es';
import React, { useLayoutEffect, useRef } from 'react';
import { isFromDB } from 'store/storeTypes';
import { useStoreCurrentRecord } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import {
  getIoDatasourceStoreProps,
  useIoDatasourceAttributesStore,
} from 'store/utils/utils';
import { isNull } from 'utils/isEmpty';
import Checkbox from '../fields/Checkbox';
import TextField from '../fields/TextField';
import { DataSourceChooser } from '../PropertiesView/property-editors/DataSourceChooserProperty';
import {
  AttributeChooser,
  MultiAttributeChooser,
} from '../PropertiesView/property-editors/ViewAttributeChooserProperty';
import { isViewOnlyDeveloper } from 'root/useUser';
import { usePlatform } from 'platform/PlatformHooks';
import WaveDots from 'utils/WaveDots';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '0 20px',
    '& > *': {
      marginTop: 8,
    },
  },
  icons: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  selected: {
    backgroundColor: theme.palette.background.default,
    '& svg': {
      fill: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
}));

const AttributeEdit = ({ appUid }: { appUid: string }): React.ReactElement => {
  const store = useIoDatasourceAttributesStore(appUid);
  const [dsAttrId, setDsAttrId] = useStateXAtom(sidebarActiveAttributeIdAtom);
  const dsid = useStateXAtomValue(sidebarActiveDatasourceIdAtom);
  if (dsAttrId) {
    // dsAttrId will be an empty string during new attribute creation
    store.read({ props: dsAttributesFilter(appUid, dsid) });
  }
  const ref = useRef<HTMLInputElement>(null);
  const classes = useStyles();
  const { isBusy, record, update } = useStoreCurrentRecord(store);
  const fromDB = isFromDB(record);
  const platform = usePlatform();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  useLayoutEffect(() => {
    // dsAttrId will be an empty string during new attribute creation
    if (dsAttrId && store.getCurrentRecordId() !== dsAttrId) {
      if (store.recordsOrder().includes(dsAttrId)) {
        store.setCurrentRecordId(dsAttrId);
      } else {
        setDsAttrId(store.getCurrentRecordId() ?? '');
      }
    }
    if (!(isBusy || fromDB)) {
      ref.current?.focus();
    }
  }, [dsAttrId, fromDB, isBusy, setDsAttrId, store]);

  const dsStore = useDataStore<IoDatasource>(getIoDatasourceStoreProps(appUid));

  const { record: curDSRecord } = useStoreCurrentRecord(dsStore);
  const isCalculatedColumn = record && record.calculatedColumn === 'Y';

  const isDummyTable = curDSRecord?.typeValue?.toLowerCase() === 'dummy';

  const ds = curDSRecord?.datasourceUid;

  if (!(record && ds)) {
    if (isBusy) {
      return <WaveDots active />;
    } else {
      return <>Missing current record!</>;
    }
  }
  const whoColumn =
    record.attributeCode &&
    fromDB &&
    ['lastUpdateDate', 'createdBy', 'creationDate', 'lastUpdatedBy'].includes(
      record.attributeCode,
    );

  const compatibleFieldTypes = !record.attributeType
    ? []
    : CompatibleFieldTypes[record.attributeType] ?? [];
  let defaultValueTip = '';
  let defaultPlaceholder = '';
  let refType = false;
  const refMd = record.refMd;
  if (record.attributeType) {
    refType = record.attributeType === 'Reference';
    if (isDateType(record.attributeType)) {
      defaultValueTip = 'Use **sysdate** to default current date';
      defaultPlaceholder = 'e.g. sysdate';
    }
    if (record.attributeType === 'String') {
      defaultValueTip = `Use any string value or one of the following pre-defined values

- **UUID** Populates a 26 char long Universally Unique Lexicographically Sortable ID

\`\`\`alert
Note: Specifying the following values, will not only force default the value, replacing any user provided value, but also appends to the where clause preventing the user from seeing or updating other data.
\`\`\`

- **OrgUID** Populates the current Organization UID from the user session
- **AppUID** Populates the current Application UID from the user session
- **UserName** Populates the current UserName from the user session
`;
      defaultPlaceholder = 'e.g. UUID or some value';
    }
  }

  let fieldTypes = FieldTypeArray;
  if (!fromDB) {
    fieldTypes = fieldTypes.filter((f) => f !== 'Reference');
  }

  return (
    <form className={classes.root} noValidate autoComplete="off">
      <TextField
        data-testid="datasourceUid"
        disabled
        label="DataSource Name"
        value={curDSRecord?.datasourceUid ?? ''}
      />
      {!fromDB ? (
        record.calculatedColumn !== 'Y' ? (
          <Alert
            severity="success"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  update({
                    allowBlank: 'Y',
                    auditEnabled: 'N',
                    calculatedColumn: 'Y',
                    insertAllowed: 'N',
                    queryAllowed: 'N',
                    selectAllowed: 'N',
                    updateAllowed: 'N',
                    columnName: "(SELECT 'SomeThing')",
                    attributeName: 'Some Thing',
                    attributeCode: 'someThing',
                  });
                }}
              >
                Yes
              </Button>
            }
          >
            Do you want to create a transient attribute instead?
          </Alert>
        ) : (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  update({
                    allowBlank: 'Y',
                    auditEnabled: 'Y',
                    calculatedColumn: 'N',
                    insertAllowed: 'Y',
                    queryAllowed: 'Y',
                    selectAllowed: 'Y',
                    updateAllowed: 'Y',
                    columnName: '',
                    attributeCode: '',
                    attributeName: '',
                  });
                }}
              >
                Yes
              </Button>
            }
          >
            Switch back to column based attribute?
          </Alert>
        )
      ) : null}
      <TextField
        data-testid="columnName"
        inputRef={ref}
        autoFocus={!fromDB}
        disabled={fromDB && record.calculatedColumn !== 'Y'}
        minRows={record.calculatedColumn === 'Y' ? 3 : 1}
        label={
          record.calculatedColumn === 'Y'
            ? `SQL Expression ( ${(record.columnName ?? '').length} / 512 )`
            : 'Column Name'
        }
        multiline={record.calculatedColumn === 'Y'}
        value={record.columnName ?? ''}
        onChange={(e) => {
          if (record.calculatedColumn === 'Y') {
            update({
              columnName: e.target.value,
            });
          } else {
            const columnName = e.target.value.endsWith('_')
              ? e.target.value
              : snakeCase(e.target.value.toLowerCase()).toUpperCase();
            const colNameLC = columnName.toLowerCase();
            const oldColNameLC = (record.columnName ?? '').toLowerCase();
            update({
              columnName,
              attributeCode:
                !record.attributeCode ||
                record.attributeCode === camelCase(oldColNameLC)
                  ? camelCase(colNameLC)
                  : record.attributeCode,
              attributeName:
                !record.attributeName ||
                record.attributeName === startCase(oldColNameLC)
                  ? startCase(colNameLC)
                  : record.attributeName,
            });
          }
        }}
        InputProps={{
          endAdornment: fromDB ? undefined : (
            <InputAdornment position="end">
              <Icon
                iconName="comment-exclamation"
                data-tip-markdown
                data-tip={`A new column will be added to the underlying table for non-transient attributes. 

To create a calculated attribute, without adding a new column to the table, check the Transient checkbox and uncheck the Selectable, Insertable & Updatable checkboxes.

You can also specify a valid SQL expression for a Selectable Transient attribute. (You must check Transient & Selectable options before entering a SQL expression)

\`\`\`alert
Note: No column will be added if the table name is 'dummy' (Transient DataSource).
\`\`\`
`}
                data-tip-delay={0}
                data-tip-width={500}
                data-tip-at="top"
              />
            </InputAdornment>
          ),
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={{
          maxLength: 512,
        }}
      />
      <TextField
        data-testid="attributeName"
        autoFocus={fromDB}
        label="Label"
        disabled={isViewOnlyUser}
        value={record.attributeName ?? ''}
        onChange={(e) =>
          update({
            attributeName: e.target.value,
            attributeCode:
              !fromDB &&
              (!record.attributeCode ||
                camelCase(record.attributeName) === record.attributeCode)
                ? camelCase(e.target.value)
                : record.attributeCode,
            columnName:
              !fromDB &&
              (!record.columnName ||
                snakeCase(record.columnName).toUpperCase() ===
                  record.columnName)
                ? snakeCase(e.target.value).toUpperCase()
                : record.columnName,
          })
        }
        inputProps={{
          maxLength: 60,
        }}
      />
      <TextField
        data-testid="attributeCode"
        disabled={fromDB}
        label="Code"
        value={record.attributeCode ?? ''}
        onChange={(e) =>
          update({
            attributeCode: e.target.value.endsWith(' ')
              ? e.target.value
              : camelCase(e.target.value),
          })
        }
        inputProps={{
          maxLength: 60,
        }}
      />
      {!fromDB ? (
        <FormControl variant="standard" fullWidth>
          <InputLabel>Type</InputLabel>
          <Select
            value={record.attributeType ?? ''}
            onChange={(e) =>
              update({ attributeType: e.target.value as FieldType })
            }
          >
            {fieldTypes.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <FormControl
          variant="standard"
          fullWidth
          disabled={compatibleFieldTypes.length === 0 || isViewOnlyUser}
        >
          <InputLabel>Type</InputLabel>
          <Select
            value={record.attributeType ?? ''}
            onChange={(e) =>
              update({ attributeType: e.target.value as FieldType })
            }
          >
            <MenuItem key={record.attributeType} value={record.attributeType}>
              {record.attributeType}
            </MenuItem>
            <MenuItem key="change_to" value="change_to" disabled>
              Change to...
            </MenuItem>
            <Divider />
            {compatibleFieldTypes.map((v) => (
              <MenuItem key={v} value={v}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {whoColumn || fromDB ? null : (
        <FormControl variant="standard" fullWidth>
          <div className={classes.icons}>
            {(
              Object.keys(FieldTypeIcons) as (keyof typeof FieldTypeIcons)[]
            ).map((k: FieldType) => {
              const { iconName, iconType } = parseIconText(FieldTypeIcons[k]);
              if (!fromDB && k === 'Reference') {
                return null;
              }
              return (
                <IconButton
                  key={k}
                  className={
                    record.attributeType === k ? classes.selected : undefined
                  }
                  data-tip={FieldTypeDesc[k]}
                  size="medium"
                  onClick={() => update({ attributeType: k })}
                  disabled={fromDB}
                  data-testid={`attributeType-${k}`}
                >
                  <Icon
                    iconName={iconName}
                    iconType={iconType}
                    width={24}
                    height={24}
                  />
                </IconButton>
              );
            })}
          </div>
        </FormControl>
      )}
      <Grid container>
        {whoColumn || record.calculatedColumn === 'Y' ? null : (
          <Grid item xs>
            <FormControlLabel
              disabled={isViewOnlyUser}
              control={
                <Checkbox
                  checked={record.auditEnabled !== 'N'}
                  onChange={(e) =>
                    update({ auditEnabled: e.target.checked ? 'Y' : 'N' })
                  }
                  data-testid="auditEnabled"
                />
              }
              label="Auditable"
            />
          </Grid>
        )}
        {whoColumn || record.calculatedColumn === 'Y' ? null : (
          <Grid item xs>
            <FormControlLabel
              disabled={isViewOnlyUser}
              control={
                <Checkbox
                  checked={record.allowBlank === 'N'}
                  onChange={(e) =>
                    update({ allowBlank: e.target.checked ? 'N' : 'Y' })
                  }
                  data-testid="allowBlank"
                />
              }
              label="Required"
            />
          </Grid>
        )}
        <Grid item xs>
          <FormControlLabel
            disabled={isViewOnlyUser}
            control={
              <Checkbox
                checked={record.queryAllowed !== 'N'}
                onChange={(e) =>
                  update({ queryAllowed: e.target.checked ? 'Y' : 'N' })
                }
                data-testid="queryAllowed"
              />
            }
            label="Searchable"
          />
        </Grid>
        <Grid item xs>
          <FormControlLabel
            disabled={isViewOnlyUser}
            control={
              <Checkbox
                checked={record.selectAllowed !== 'N'}
                onChange={(e) =>
                  update({ selectAllowed: e.target.checked ? 'Y' : 'N' })
                }
                data-testid="selectAllowed"
              />
            }
            label="Selectable"
          />
        </Grid>
        {whoColumn || refType ? null : (
          <Grid item xs>
            <FormControlLabel
              disabled={isViewOnlyUser}
              control={
                <Checkbox
                  disabled={fromDB}
                  checked={record.calculatedColumn === 'Y'}
                  onChange={(e) =>
                    update({ calculatedColumn: e.target.checked ? 'Y' : 'N' })
                  }
                  data-testid="calculatedColumn"
                />
              }
              label="Transient"
            />
          </Grid>
        )}
        {whoColumn || record.calculatedColumn === 'Y' ? null : (
          <Grid item xs>
            <FormControlLabel
              disabled={isViewOnlyUser}
              control={
                <Checkbox
                  checked={record.insertAllowed !== 'N'}
                  onChange={(e) =>
                    update({ insertAllowed: e.target.checked ? 'Y' : 'N' })
                  }
                  data-testid="insertAllowed"
                />
              }
              label="Insertable"
            />
          </Grid>
        )}
        {whoColumn || record.calculatedColumn === 'Y' ? null : (
          <Grid item xs>
            <FormControlLabel
              disabled={isViewOnlyUser}
              control={
                <Checkbox
                  checked={record.updateAllowed !== 'N'}
                  onChange={(e) =>
                    update({ updateAllowed: e.target.checked ? 'Y' : 'N' })
                  }
                  data-testid="updateAllowed"
                />
              }
              label="Updatable"
            />
          </Grid>
        )}
        {fromDB && !whoColumn && record.calculatedColumn !== 'Y' ? (
          <Grid item xs>
            <FormControlLabel
              disabled={isViewOnlyUser}
              control={
                <Checkbox
                  checked={record.primaryKey !== 'N'}
                  onChange={(e) =>
                    update({ primaryKey: e.target.checked ? 'Y' : 'N' })
                  }
                  data-testid="primaryKey"
                />
              }
              label="Primary Key"
            />
          </Grid>
        ) : null}
        <Grid item xs>
          <FormControlLabel
            disabled={isViewOnlyUser}
            control={
              <Checkbox
                checked={record.exportAllowed !== 'N'}
                onChange={(e) =>
                  update({ exportAllowed: e.target.checked ? 'Y' : 'N' })
                }
                data-testid="exportAllowed"
              />
            }
            label="Exportable"
          />
        </Grid>
        {record.attributeType === 'Integer' &&
        record.calculatedColumn !== 'Y' ? (
          <Grid item xs>
            <FormControlLabel
              disabled={isViewOnlyUser}
              control={
                <Checkbox
                  checked={record.autoSequence === 'Y'}
                  onChange={(e) =>
                    update({ autoSequence: e.target.checked ? 'Y' : 'N' })
                  }
                  data-testid="autoSequence"
                />
              }
              label="Auto Increment"
            />
          </Grid>
        ) : null}
      </Grid>
      <TextField
        data-testid="paramName"
        label="Param Name"
        value={record.paramName ?? ''}
        onChange={(e) =>
          update({
            paramName: e.target.value,
          })
        }
        inputProps={{
          maxLength: 60,
        }}
        helperText="Please specify the name of the column as referenced in the package. By default, when the package is called, the attribute code is prefixed with 'P_'. Provide the name here if you wish to refer to it differently"
      />
      {whoColumn || refType ? null : (
        <TextField
          disabled={isViewOnlyUser}
          data-testid="defaultValue"
          label="Default Value"
          value={record.defaultValue ?? ''}
          onChange={(e) => update({ defaultValue: e.target.value })}
          placeholder={defaultPlaceholder}
          InputProps={{
            endAdornment: defaultValueTip ? (
              <InputAdornment position="end">
                <Icon
                  iconName="comment-exclamation"
                  data-tip={defaultValueTip}
                  data-tip-markdown
                  data-tip-delay={0}
                  data-tip-width={600}
                  data-tip-at="top"
                />
              </InputAdornment>
            ) : undefined,
          }}
        />
      )}
      {(record.attributeType === 'String' ||
        record.attributeType === 'Textarea' ||
        record.attributeType === 'Decimal') &&
      !whoColumn ? (
        <TextField
          disabled={
            (fromDB && !isCalculatedColumn && !isDummyTable) || isViewOnlyUser
          }
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          data-testid="maxLength"
          label="Max Length"
          value={record.maxLength ?? ''}
          onChange={(e) => {
            let maxLength = isNull(e.target.value)
              ? undefined
              : parseInt(e.target.value, 10);
            maxLength = Number.isNaN(maxLength) ? undefined : maxLength;
            update({
              maxLength,
            });
          }}
        />
      ) : null}
      {record.attributeType === 'Decimal' ? (
        <TextField
          disabled={fromDB || isViewOnlyUser}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          data-testid="scale"
          label="Scale"
          value={record.scale ?? ''}
          onChange={(e) => {
            let scale = isNull(e.target.value)
              ? undefined
              : parseInt(e.target.value, 10);
            scale = Number.isNaN(scale) ? undefined : scale;
            update({
              scale,
            });
          }}
        />
      ) : null}
      {record.attributeType === 'Integer' ||
      record.attributeType === 'Double' ? (
        <FormControl disabled={isViewOnlyUser}>
          <FormLabel>Byte Length</FormLabel>
          <RadioGroup
            row
            value={`${record.maxLength ?? 8}`}
            onChange={(e) => {
              let maxLength = isNull(e.target.value)
                ? undefined
                : parseInt(e.target.value, 10);
              maxLength = Number.isNaN(maxLength) ? undefined : maxLength;
              if (maxLength === 2 || maxLength === 4 || maxLength === 8) {
                update({
                  maxLength,
                });
              }
            }}
          >
            <FormControlLabel
              data-testid="bl-2"
              value="2"
              control={<Radio />}
              label="2"
            />
            <FormControlLabel
              data-testid="bl-4"
              value="4"
              control={<Radio />}
              label="4"
            />
            <FormControlLabel
              data-testid="bl-8"
              value="8"
              control={<Radio />}
              label="8"
            />
            {record.maxLength && ![2, 4, 8].includes(record.maxLength) ? (
              <FormControlLabel
                disabled
                data-testid="bl-x"
                value={record.maxLength}
                control={<Radio />}
                label={record.maxLength}
              />
            ) : null}
          </RadioGroup>
        </FormControl>
      ) : null}

      {curDSRecord.datasourceType === 'Table' && curDSRecord.extTypeValue ? (
        <FormControl fullWidth>
          <FormControlLabel
            control={
              <Checkbox
                checked={record.outParam === 'Y'}
                onChange={(e) =>
                  update({ outParam: e.target.checked ? 'Y' : 'N' })
                }
                data-testid="outParam"
              />
            }
            label="Out Parameter"
          />
        </FormControl>
      ) : null}

      {refType ? (
        <>
          <Typography>Reference Details</Typography>
          <Divider />
        </>
      ) : null}
      {refType ? (
        <DataSourceChooser
          appUid={appUid}
          testid="refDatasourceUid"
          value={refMd?.datasourceUid ?? ''}
          onChange={(_ds) => {
            const md: ReferenceMetadata = {
              datasourceUid: _ds,
              attributeCode: '',
              mappings: [],
              selectAttributes: [],
              attributeType: 'String',
            };
            update({
              refMd: md,
            });
          }}
          label="Reference DataSource"
          required
          helpText=""
          includeCloudIODataSources={false}
        />
      ) : null}
      {refType && refMd && refMd.datasourceUid ? (
        <AttributeChooser
          ds={refMd.datasourceUid}
          appUid={appUid}
          helpText={`Choose an attribute in the reference DataSource to which ${record.attributeName} refers to`}
          label="Reference Attribute"
          onChange={(attributeCode, option) => {
            update({
              refMd: {
                ...refMd,
                attributeCode,
                attributeType:
                  option?.dataType === 'Number'
                    ? 'Integer'
                    : option?.dataType ?? 'String',
              },
            });
          }}
          required
          testid="refAttributeCode"
          types="ALL"
          autoFocus={!refMd.attributeCode}
          value={refMd.attributeCode ?? ''}
          dsAppUid={undefined}
        />
      ) : null}
      {refType && refMd && refMd.datasourceUid && refMd.attributeCode ? (
        <MultiAttributeChooser
          ds={refMd.datasourceUid}
          appUid={appUid}
          helpText={`Choose one or more attributes from ${refMd.datasourceUid} to be included while fetching ${ds} data`}
          label="Select Attributes"
          onChange={(selectAttributes) => {
            update({
              refMd: { ...refMd, selectAttributes },
            });
          }}
          required
          testid="selectAttributes"
          types="ALL"
          value={refMd.selectAttributes}
          dsAppUid={undefined}
        />
      ) : null}
      {refType && refMd && refMd.datasourceUid && refMd.attributeCode ? (
        <>
          <Typography>Additional Joins</Typography>
          <Divider />
        </>
      ) : null}
      {refType && refMd && refMd.datasourceUid && refMd.attributeCode
        ? refMd.mappings.map((mapping, index) => (
            <Mapping
              key={mapping.attributeCode}
              index={index}
              refDs={refMd.datasourceUid}
              ds={ds}
              mapping={mapping}
              appUid={appUid}
              onChange={(m, i) => {
                const mappings = [...refMd.mappings];
                mappings[i] = m;
                update({
                  refMd: { ...refMd, mappings },
                });
              }}
              autoFocus={refMd.mappings.length === index + 1}
              removeMapping={(i) => {
                const mappings = [...refMd.mappings];
                mappings.splice(i, 1);
                update({
                  refMd: { ...refMd, mappings },
                });
              }}
            />
          ))
        : null}
      {refType && refMd && refMd.datasourceUid && refMd.attributeCode ? (
        <Mapping
          index={-1}
          refDs={refMd.datasourceUid}
          ds={ds}
          mapping={{ attributeCode: '', refAttributeCode: '' }}
          appUid={appUid}
          onChange={(mapping, index) => {
            update({
              refMd: { ...refMd, mappings: [...refMd.mappings, mapping] },
            });
          }}
          removeMapping={emptyFunction}
        />
      ) : null}
    </form>
  );
};

function Mapping({
  appUid,
  mapping,
  ds,
  index,
  refDs,
  onChange,
  autoFocus,
  removeMapping,
}: {
  autoFocus?: boolean;
  appUid: string;
  mapping: { attributeCode: string; refAttributeCode: string };
  ds: string;
  index: number;
  refDs: string;
  onChange: (
    _mapping: { attributeCode: string; refAttributeCode: string },
    _index: number,
  ) => void;
  removeMapping: (i: number) => void;
}) {
  return (
    <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
      <AttributeChooser
        ds={ds}
        appUid={appUid}
        helpText=""
        label={`${ds} Attribute`}
        onChange={(attributeCode) => {
          onChange({ ...mapping, attributeCode }, index);
        }}
        types={['String', 'Integer', 'UserID', 'YN', 'TF']}
        value={mapping.attributeCode ?? ''}
        autoFocus={
          autoFocus && !mapping.attributeCode && !!mapping.refAttributeCode
        }
        dsAppUid={undefined}
      />
      <AttributeChooser
        ds={refDs}
        appUid={appUid}
        helpText=""
        label={`${refDs} Attribute`}
        onChange={(refAttributeCode) => {
          onChange({ ...mapping, refAttributeCode }, index);
        }}
        types={['String', 'Integer', 'UserID', 'YN', 'TF']}
        autoFocus={autoFocus && !mapping.refAttributeCode}
        value={mapping.refAttributeCode ?? ''}
        dsAppUid={undefined}
      />
      <IconButton disabled={index === -1} onClick={(e) => removeMapping(index)}>
        <span aria-hidden="false" style={{ display: 'none' }}>
          Remove Mapping
        </span>
        <Icon iconName="times" />
      </IconButton>
    </Box>
  );
}

export default AttributeEdit;
